import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import type { JsonObject } from "@/domains/common/types";
import { CacheKey } from "@/modules/cache/constants";
import { LEGACY_GLOBAL_CACHE_INSTANCE } from "@/modules/cache/globalCache";
import { audioRecordingModule } from "@/modules/audio-recording";
import { electronModule } from "@/modules/electron";
import { TranscriptionStatus } from "@/store/audio/recording";
import type { AppSubStoreArgs } from "@/store/types";
import { AppSubStore } from "@/store/types";
import { AppStoreAudioRecordingStore } from "@/store/audio/recording/audio-recording.store";
import { AppStoreAudioSuggestionStore } from "@/store/audio/suggestion";
import { AppStoreAudioTranscriptionStore } from "@/store/audio/transcription";
import {
  AppStoreAudioTransformingStore,
  AudioRecordingTransformingState,
} from "@/store/audio/transforming";
import { AppStoreAudioUiStore } from "@/store/audio/ui/audio-ui.store";
import { AppStoreAudioUploadingStore, AudioRecordingUploadingState } from "@/store/audio/uploading";
import { AppStoreAudioPipelineStore } from "@/store/audio/pipeline/audio-pipeline.store";
import { buildAudioRecordingArtifactDiagnostics } from "@/store/audio/audio-recording-artifact-diagnostics";
import {
  type IAudioStore,
  type TrashRecordingContext,
  TrashRecordingReason,
} from "@/store/audio/audio.types";
import { AudioRecorderLockHandle } from "@/modules/audio-recording/audio-recorder-lock";
import { logger } from "@/modules/logger";
import { AudioRecorderLockingError } from "@/domains/errors/kinds/contention";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import {
  AudioRecordingObservable,
  AudioRecordingModelData,
  AudioRecordingProcessingStatus,
  AudioRecordingRecordingStatus,
  AudioRecordingStrategy,
  AudioRecordingUploadStatus,
  IndexedAudioRecordingSyncUpdateValue,
  getAudioRecordingStrategy,
} from "@/store/audio-recordings";
import { TrackedEvent, type VoiceSessionEventParams } from "@/domains/metrics/tracked-event";
import { trackEvent } from "@/domains/metrics";
import { CustomEventName, dispatchCustomEvent } from "@/modules/custom-event";
import { clientEnvModule } from "@/modules/client-env";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { DateTime } from "luxon";
import { uuidModule } from "@/modules/uuid";
import { deleteNoteSource } from "@/actions/deleteNoteSource";
import { DeleteSourceOperation } from "@/store/sync/operations/sources/DeleteSourceOperation";
import { STEP_IDS } from "@/store/getting-started";
import {
  AudioRecordingStrategyDriver,
  DesktopArtifactRecordingStrategyDriver,
  UploadableAudioRecordingArtifactStatus,
  WebArtifactRecordingStrategyDriver,
} from "@/store/audio/recording/strategies";

type RecordingLockAction = {
  audioRecordingId: Uuid;
  action: () => Promise<void>;
  toastMessage: string;
};

type CreateAudioRecordingArgs = {
  audioRecordingId?: Uuid;
  preparedRecording?: PreparedRecording;
  expandVoicePopover?: boolean;
  associatedNoteId?: Uuid | null;
  associatedMeetingWindowId?: string;
  calendarEventId?: string;
  config?: {
    showOnboardingTooltip: boolean;
    forceWebMediaController?: boolean;
  };
};

type ResolveNewRecordingStrategyArgs = {
  forceWebMediaController?: boolean;
};

export class AppStoreAudioStore extends AppSubStore implements IAudioStore {
  ui: AppStoreAudioUiStore;
  recording: AppStoreAudioRecordingStore;
  transcription: AppStoreAudioTranscriptionStore;
  uploading: AppStoreAudioUploadingStore;
  transforming: AppStoreAudioTransformingStore;
  pipeline: AppStoreAudioPipelineStore;
  suggestion: AppStoreAudioSuggestionStore;

  private activeRecordingLockHandle: AudioRecorderLockHandle | null = null;
  private activeRecordingIdValue: Uuid | null = null;
  private activeRecordingNoteIdValue: Uuid | null = null;
  private deactivateOrphanedActiveRecordingReaction: IReactionDisposer;
  private readonly preparedRecordingsMap = new Map<Uuid, PreparedRecording>();
  private readonly webArtifactStrategyDriver: WebArtifactRecordingStrategyDriver;
  private readonly desktopArtifactStrategyDriver: DesktopArtifactRecordingStrategyDriver;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.ui = new AppStoreAudioUiStore(injectedDeps);
    this.ui.initialize();
    this.webArtifactStrategyDriver = new WebArtifactRecordingStrategyDriver({
      webAudioArtifacts: this.store.dexie.repositories.webAudioArtifacts,
    });
    this.desktopArtifactStrategyDriver = new DesktopArtifactRecordingStrategyDriver();

    this.uploading = new AppStoreAudioUploadingStore({
      ...injectedDeps,
      config: {
        onUploadComplete: () => {},
      },
    });

    this.recording = new AppStoreAudioRecordingStore({
      ...injectedDeps,
      webArtifactStrategyDriver: this.webArtifactStrategyDriver,
      desktopArtifactStrategyDriver: this.desktopArtifactStrategyDriver,
      uiStore: this.ui,
      config: {
        onRecordingCompleted: ({ audioRecordingId }) => {
          this.pipeline.onRecordingCompleted({ audioRecordingId });
        },
      },
    });

    this.transcription = new AppStoreAudioTranscriptionStore(injectedDeps);

    this.transforming = new AppStoreAudioTransformingStore({
      ...injectedDeps,
      transcriptionStore: this.transcription,
    });

    this.pipeline = new AppStoreAudioPipelineStore(injectedDeps);

    this.suggestion = new AppStoreAudioSuggestionStore({
      ...injectedDeps,
      uploadingStore: this.uploading,
      transformingStore: this.transforming,
    });

    makeObservable<
      this,
      | "activeRecordingLockHandle"
      | "withRecordingTabLock"
      | "obtainAudioRecordingLock"
      | "releaseAudioRecordingLock"
      | "setActiveAudioRecording"
      | "clearActiveAudioRecording"
      | "activeRecordingIdValue"
      | "activeRecordingNoteIdValue"
      | "hasLockInCurrentTab"
      | "maybeObtainAudioRecordingLock"
      | "cleanupActiveDesktopRecordingAfterRecoveryFailure"
      | "deactivateOrphanedActiveRecordingReaction"
      | "shouldBlockRecordingStart"
      | "resolvePreparedRecording"
      | "buildVoiceSessionEventParams"
      | "promptReplaceActiveRecording"
      | "preparedRecordingsMap"
      | "webArtifactStrategyDriver"
      | "desktopArtifactStrategyDriver"
      | "reconcilePersistedAudioState"
      | "reconcileRecordingState"
      | "hasTerminalProcessingStatus"
      | "hasTerminalUploadStatus"
      | "hasUploadStatusThatBlocksProcessing"
      | "buildTrashRecordingDiagnostics"
      | "deleteSourceForRecording"
      | "resolveNewRecordingStrategy"
      | "getStrategyDriver"
      | "getUploadableArtifactStatus"
    >(this, {
      recording: observable,
      transcription: observable,
      uploading: observable,
      transforming: observable,
      pipeline: observable,
      suggestion: observable,
      ui: observable,
      activeRecordingLockHandle: observable,
      activeRecordingId: computed,
      activeRecordingNoteId: computed,
      activeRecording: computed,
      hasActiveRecording: computed,
      createAndStartAudioRecording: action,
      prepareRecording: action,
      pauseRecording: action,
      resumeRecording: action,
      finishRecording: action,
      trashRecording: action,
      startSourceUpload: action,
      processRecording: action,
      initialize: action,
      withRecordingTabLock: action,
      obtainAudioRecordingLock: action,
      releaseAudioRecordingLock: action,
      setActiveAudioRecording: action,
      clearActiveAudioRecording: action,
      getUploadStateForRecording: false,
      getTransformStateForRecording: false,
      _activateRecording: action,
      _deactivateRecording: action,
      dispose: action,
      resetState: action,
      rehydrateAndObtainAudioRecordingLockIfNecessary: action,
      maybeObtainAudioRecordingLock: action,
      cleanupActiveDesktopRecordingAfterRecoveryFailure: action,
      activeRecordingIdValue: observable,
      activeRecordingNoteIdValue: observable,
      hasLockInCurrentTab: computed,
      deactivateOrphanedActiveRecordingReaction: observable.ref,
      ensureActiveRecordingStateIsValid: action,
      shouldBlockRecordingStart: action,
      resolvePreparedRecording: false,
      buildVoiceSessionEventParams: false,
      promptReplaceActiveRecording: false,
      preparedRecordingsMap: false,
      webArtifactStrategyDriver: false,
      desktopArtifactStrategyDriver: false,
      getPreparedRecordingById: false,
      getPreparedRecording: false,
      reconcilePersistedAudioState: false,
      reconcileRecordingState: false,
      hasTerminalProcessingStatus: false,
      hasTerminalUploadStatus: false,
      hasUploadStatusThatBlocksProcessing: false,
      buildTrashRecordingDiagnostics: false,
      deleteSourceForRecording: false,
      resolveNewRecordingStrategy: false,
      getStrategyDriver: false,
      getUploadableArtifactStatus: false,
    });

    /**
     * If our audio recording becomes orphaned, then we automatically trash it.
     */
    this.deactivateOrphanedActiveRecordingReaction = reaction(
      () => {
        if (!this.hasLockInCurrentTab) {
          return { shouldDeactivate: false };
        }

        const activeRecordingId = this.activeRecordingIdValue;
        if (!activeRecordingId) {
          return { shouldDeactivate: false };
        }

        const recording = this.store.audioRecordings.get(activeRecordingId);
        if (!recording) {
          return { shouldDeactivate: false };
        }

        /**
         * If the audio recording has no associatedNoteId, then that is alright.
         * (Note that this kind of state is currently only used for internal/testing purposes)
         */
        const noteId = recording.associatedNoteId;
        if (!noteId) {
          return { shouldDeactivate: false };
        }

        const note = this.store.notes.getNoteObservableById({ noteId });
        const doesNotExist = this.store.notes.doesNotExist(noteId);

        const isTrashedOrDeleted = note ? note.isTrashed || note.isDeleted : false;

        return {
          audioRecordingId: activeRecordingId,
          shouldDeactivate: isTrashedOrDeleted || doesNotExist,
        };
      },
      async ({ audioRecordingId, shouldDeactivate }, _prev) => {
        if (!audioRecordingId) return;
        if (!shouldDeactivate) return;

        try {
          await this.trashRecording({
            audioRecordingId,
            context: { reason: TrashRecordingReason.NoteRemoved },
          });
        } catch (unknownErr) {
          logger.warn({
            message:
              "[AppStoreAudioStore] failed to deactivate orphaned active recording after note removal",
            info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
          });
        }
      }
    );
  }

  getPreparedRecordingById(audioRecordingId: Uuid): PreparedRecording | undefined {
    const preparedRecording = this.preparedRecordingsMap.get(audioRecordingId);
    if (!preparedRecording) return undefined;
    if (preparedRecording.expiresAt < DateTime.local()) {
      this.preparedRecordingsMap.delete(audioRecordingId);
      return undefined;
    }
    return preparedRecording;
  }

  getPreparedRecording(): PreparedRecording | undefined {
    if (this.activeRecordingId) return;

    const preparedRecording = this.recording.getPreparedRecording();
    this.preparedRecordingsMap.set(preparedRecording.audioRecordingId, preparedRecording);
    return preparedRecording;
  }

  get activeRecordingId(): Uuid | null {
    if (!this.hasLockInCurrentTab) return null;

    return this.activeRecordingIdValue ?? null;
  }

  get activeRecording(): AudioRecordingObservable | null {
    if (!this.hasLockInCurrentTab) return null;

    const activeId = this.activeRecordingIdValue;
    if (!activeId) return null;

    return this.store.audioRecordings.get(activeId) ?? null;
  }

  get activeRecordingNoteId(): Uuid | null {
    if (this.activeRecordingNoteIdValue) return this.activeRecordingNoteIdValue;
    const recording = this.activeRecording;
    if (!recording) return null;

    return (recording.associatedNoteId as Uuid | undefined) ?? null;
  }

  get hasActiveRecording(): boolean {
    return this.activeRecordingId !== null;
  }

  private resolvePreparedRecording({
    providedPreparedRecording,
    providedAudioRecordingId,
    requestedAudioRecordingId,
  }: {
    providedPreparedRecording?: PreparedRecording;
    providedAudioRecordingId?: Uuid;
    requestedAudioRecordingId: Uuid | null;
  }): PreparedRecording {
    if (providedPreparedRecording) {
      return providedPreparedRecording;
    }

    const cachedPreparedRecording = requestedAudioRecordingId
      ? this.preparedRecordingsMap.get(requestedAudioRecordingId)
      : undefined;
    if (cachedPreparedRecording) {
      return cachedPreparedRecording;
    }

    if (providedAudioRecordingId) {
      return this.recording.getPreparedRecording(providedAudioRecordingId);
    }

    return this.recording.getPreparedRecording();
  }

  private promptReplaceActiveRecording({
    activeRecordingId,
    requestedAudioRecordingId,
    associatedNoteId,
    associatedMeetingWindowId,
    expandVoicePopover,
    config,
  }: {
    activeRecordingId: Uuid;
    requestedAudioRecordingId: Uuid | null;
    associatedNoteId?: Uuid | null;
    associatedMeetingWindowId?: string;
    expandVoicePopover: boolean;
    config?: CreateAudioRecordingArgs["config"];
  }): void {
    this.ui.promptToReplaceActiveRecording({
      onConfirm: async () => {
        await this.finishRecording({ audioRecordingId: activeRecordingId });

        if (requestedAudioRecordingId) {
          this.preparedRecordingsMap.delete(requestedAudioRecordingId);
        }

        await this.createAndStartAudioRecording({
          audioRecordingId: requestedAudioRecordingId ?? undefined,
          associatedNoteId,
          associatedMeetingWindowId,
          expandVoicePopover,
          config,
        });
      },
    });
  }

  async createAndStartAudioRecording({
    preparedRecording: providedPreparedRecording,
    audioRecordingId: providedAudioRecordingId,
    expandVoicePopover = true,
    associatedNoteId,
    associatedMeetingWindowId,
    calendarEventId,
    config,
  }: CreateAudioRecordingArgs): Promise<void> {
    if (clientEnvModule.isMobileBrowser()) {
      toastModule.triggerToast({
        content: "Voice recording is not available on mobile browsers",
        toastId: "voice-recording-not-available-on-mobile-browsers",
      });
      return;
    }

    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return;
    }

    if (!providedPreparedRecording) {
      this.store.electron.pushRecallDebugEvent({}, "in-app-voice-recording");
    }
    const requestedAudioRecordingId =
      providedPreparedRecording?.audioRecordingId ?? providedAudioRecordingId ?? null;
    const activeRecordingId = this.activeRecordingIdValue;

    // If this tab already owns the lock and a different recording is active,
    // prompt before creating/using a PreparedRecording to avoid Recall SDK races.
    if (
      this.hasLockInCurrentTab &&
      activeRecordingId &&
      requestedAudioRecordingId !== activeRecordingId
    ) {
      this.promptReplaceActiveRecording({
        activeRecordingId,
        requestedAudioRecordingId,
        associatedNoteId,
        associatedMeetingWindowId,
        expandVoicePopover,
        config,
      });
      return;
    }

    const preparedRecording = this.resolvePreparedRecording({
      providedPreparedRecording,
      providedAudioRecordingId,
      requestedAudioRecordingId,
    });

    const audioRecordingId = preparedRecording.audioRecordingId;
    this.preparedRecordingsMap.set(audioRecordingId, preparedRecording);

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        // State can change after the early check, so we re-check under the lock.
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.promptReplaceActiveRecording({
            activeRecordingId,
            requestedAudioRecordingId: audioRecordingId,
            associatedNoteId,
            associatedMeetingWindowId,
            expandVoicePopover,
            config,
          });
          return;
        }

        if (config?.showOnboardingTooltip) {
          this.ui.setShowOnboardingTooltip(true);
        }

        logger.info({
          message: "[AppStoreAudioStore] createAndStartAudioRecording",
          info: { associatedMeetingWindowId: associatedMeetingWindowId ?? null },
        });

        const recordingStrategy = this.resolveNewRecordingStrategy({
          forceWebMediaController: config?.forceWebMediaController ?? false,
        });

        const audioRecording = await this.store.dexie.repositories.audioRecordings.upsert({
          id: audioRecordingId,
          data: {
            associatedNoteId: associatedNoteId ?? null,
            recordingStrategy,
          },
        });

        await this.prepareRecording({ audioRecordingId: audioRecording.id });

        await this.setActiveAudioRecording({ audioRecordingId, associatedNoteId });

        await this.recording.start({
          preparedRecording,
          detectedMeetingWindowId: associatedMeetingWindowId,
          calendarEventId,
          forceWebMediaController: config?.forceWebMediaController ?? false,
        });

        if (expandVoicePopover && associatedNoteId) {
          dispatchCustomEvent(CustomEventName.ExpandVoicePopover, {
            noteId: associatedNoteId,
          });
        }

        const voiceSessionEventParams = await this.buildVoiceSessionEventParams({
          audioRecordingId: audioRecording.id as Uuid,
          associatedNoteId,
          recordingStrategy,
        });
        trackEvent(TrackedEvent.VoiceSessionStart, voiceSessionEventParams);

        if (voiceSessionEventParams.is_meeting_recording) {
          this.store.gettingStarted?.markStepComplete(STEP_IDS.RECORD_MEETING);
        }
      },
      toastMessage: "Unable to start - Mem is recording on a different tab.",
    });
  }

  private resolveNewRecordingStrategy({
    forceWebMediaController = false,
  }: ResolveNewRecordingStrategyArgs): AudioRecordingStrategy {
    const shouldUseDesktopArtifactRecording =
      this.store.interface.isDesktop &&
      !this.store.electron.isIntelMacDesktop &&
      !forceWebMediaController;

    if (shouldUseDesktopArtifactRecording) {
      return AudioRecordingStrategy.DesktopArtifact;
    }

    return AudioRecordingStrategy.WebArtifact;
  }

  private getStrategyDriver({
    recordingStrategy,
  }: {
    recordingStrategy: AudioRecordingStrategy;
  }): AudioRecordingStrategyDriver {
    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return this.desktopArtifactStrategyDriver;
    }

    return this.webArtifactStrategyDriver;
  }

  async getUploadableArtifactStatus({
    audioRecordingId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
  }): Promise<UploadableAudioRecordingArtifactStatus> {
    return this.getStrategyDriver({ recordingStrategy }).getUploadableArtifactStatus({
      audioRecordingId,
    });
  }

  async prepareRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.ui.resetRecording();
    this.recording.isPaused = false;
    this.recording.waveformSamples = null;
    this.recording.setMicrophoneState({ label: null, isDisconnected: false });
    this.recording.setVisualizer(null);
    this.recording.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
    this.recording.setTranscriptionUnsupported(false);
  }

  private async buildVoiceSessionEventParams({
    audioRecordingId,
    associatedNoteId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    associatedNoteId?: Uuid | null;
    recordingStrategy?: AudioRecordingStrategy;
  }): Promise<VoiceSessionEventParams> {
    const audioRecording =
      this.store.audioRecordings.get(audioRecordingId) ??
      (await this.store.audioRecordings.getAsync(audioRecordingId));
    const noteId = associatedNoteId ?? audioRecording?.associatedNoteId ?? null;
    const note = noteId ? await this.store.notes.getAsync(noteId as Uuid) : undefined;
    const hasCalendarEventSource = note?.hasCalendarEventSource ?? false;

    return {
      audio_recording_id: audioRecordingId,
      note_id: noteId,
      recording_strategy: recordingStrategy ?? audioRecording?.recordingStrategy ?? null,
      has_calendar_event_source: hasCalendarEventSource,
      is_meeting_recording: hasCalendarEventSource,
    };
  }

  async pauseRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    /**
     * Note that we don't release the lock here.
     * We only release the lock when the recording is stopped (either by finishing or by trashing).
     */
    try {
      await this.recording.pause({ audioRecordingId });
      trackEvent(
        TrackedEvent.VoiceSessionPause,
        await this.buildVoiceSessionEventParams({ audioRecordingId })
      );
    } catch (unknownErr) {
      toastModule.triggerToast({
        content: "Unable to pause recording",
        toastId: audioRecordingId,
      });

      logger.warn({
        message: "[AppStoreAudioStore] pauseRecording: failed to pause recording",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });
    }
  }

  async resumeRecording({
    audioRecordingId,
    preparedRecording: providedPreparedRecording,
  }: {
    audioRecordingId: Uuid;
    preparedRecording?: PreparedRecording;
  }): Promise<void> {
    if (!this.store.systemPermissions.hasRequiredVoicePermissions) {
      this.store.systemPermissions.ui.openVoicePermissionsModal();
      return;
    }

    const preparedRecording =
      providedPreparedRecording ??
      this.preparedRecordingsMap.get(audioRecordingId) ??
      this.recording.getPreparedRecording(audioRecordingId);

    this.preparedRecordingsMap.set(audioRecordingId, preparedRecording);

    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        if (this.shouldBlockRecordingStart({ audioRecordingId })) {
          const activeRecordingId = this.activeRecordingIdValue;
          if (!activeRecordingId) return;

          this.ui.promptToReplaceActiveRecording({
            onConfirm: async () => {
              await this.finishRecording({ audioRecordingId: activeRecordingId });

              await this.resumeRecording({
                audioRecordingId,
                preparedRecording,
              });
            },
          });

          return;
        }

        /** Should be a no-op but included for completeness */
        await this.setActiveAudioRecording({ audioRecordingId });

        await this.recording.resume({ preparedRecording });
        trackEvent(
          TrackedEvent.VoiceSessionResume,
          await this.buildVoiceSessionEventParams({ audioRecordingId })
        );
      },
      toastMessage: "Unable to resume - Mem is recording on a different tab.",
    });
  }

  private shouldBlockRecordingStart({
    audioRecordingId: requestedRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): boolean {
    if (!this.hasActiveRecording) return false;

    const activeRecordingId = this.activeRecordingIdValue;
    if (!activeRecordingId) return false;

    return requestedRecordingId !== activeRecordingId;
  }

  async finishRecording({
    audioRecordingId,
    detectedMeetingWindowId,
  }: {
    audioRecordingId: Uuid;
    detectedMeetingWindowId?: string;
  }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        /**
         * Note that we aren't currently using the `detectedMeetingWindowId` here, we just
         * include it for logging purposes.
         */
        logger.info({
          message: "[AppStoreAudioStore] finishRecording",
          info: { audioRecordingId, detectedMeetingWindowId: detectedMeetingWindowId ?? null },
        });

        await this.recording.stop({ audioRecordingId });
        trackEvent(
          TrackedEvent.VoiceSessionDone,
          await this.buildVoiceSessionEventParams({ audioRecordingId })
        );

        await this.clearActiveAudioRecording({
          audioRecordingId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to save this recording - Mem is recording on a different tab.",
    });
  }

  async trashRecording({
    audioRecordingId,
    context = { reason: TrashRecordingReason.UserDelete },
  }: {
    audioRecordingId: Uuid;
    context?: TrashRecordingContext;
  }): Promise<void> {
    await this.withRecordingTabLock({
      audioRecordingId,
      action: async () => {
        const recording = await this.store.dexie.repositories.audioRecordings.getById({
          id: audioRecordingId,
        });
        const associatedNoteId = recording?.data.associated_note_id ?? null;
        const isActiveRecording = this.activeRecordingIdValue === audioRecordingId;
        const pipelineSnapshot = this.pipeline.getSnapshot(audioRecordingId);

        if (!recording) {
          logger.warn({
            message: "[AppStoreAudioStore] trashRecording called without a recording row",
            info: {
              audioRecordingId,
              context,
              isActiveRecording,
              pipelineStage: pipelineSnapshot?.stage ?? null,
              pipelineTerminalOutcome: pipelineSnapshot?.terminalOutcome ?? null,
            },
          });
        } else {
          try {
            logger.info({
              message: "[AppStoreAudioStore] trashing recording and deleting local artifacts",
              info: await this.buildTrashRecordingDiagnostics({
                audioRecordingId,
                modelData: recording.data,
                context,
                associatedNoteId,
                isActiveRecording,
                pipelineSnapshot,
              }),
            });
          } catch (error) {
            logger.warn({
              message: "[AppStoreAudioStore] Failed to capture trashRecording diagnostics",
              info: {
                audioRecordingId,
                context,
                error: objectModule.safeErrorAsJson(error as Error),
              },
            });
          }
        }

        await this.recording.clear({ audioRecordingId });

        if (isActiveRecording) {
          await this.clearActiveAudioRecording({
            audioRecordingId,
          });
        }

        await this.store.dexie.repositories.audioRecordings.trash({ id: audioRecordingId });
        this.uploading.clearProgress({ audioRecordingId });
        this.transforming.clearTransformState({ audioRecordingId });
        this.pipeline.clearSnapshot({ audioRecordingId });

        await this.deleteSourceForRecording({
          audioRecordingId,
          noteId: associatedNoteId,
        });
        await this.releaseAudioRecordingLock({ audioRecordingId });
      },
      toastMessage: "Unable to delete this recording - Mem is recording on a different tab.",
    });
  }

  private async buildTrashRecordingDiagnostics({
    audioRecordingId,
    modelData,
    context,
    associatedNoteId,
    isActiveRecording,
    pipelineSnapshot,
  }: {
    audioRecordingId: Uuid;
    modelData: AudioRecordingModelData;
    context: TrashRecordingContext;
    associatedNoteId: Uuid | null;
    isActiveRecording: boolean;
    pipelineSnapshot: ReturnType<AppStoreAudioPipelineStore["getSnapshot"]>;
  }): Promise<JsonObject> {
    const recordingStrategy = getAudioRecordingStrategy({ modelData });
    const recordingStrategyDriver = this.getStrategyDriver({ recordingStrategy });

    const [artifactSummary, uploadableArtifactStatus] = await Promise.all([
      recordingStrategyDriver.getArtifactDebugSummary({ audioRecordingId }),
      recordingStrategyDriver.getUploadableArtifactStatus({ audioRecordingId }),
    ]);

    return buildAudioRecordingArtifactDiagnostics({
      diagnosticsSource: "trash-recording",
      audioRecordingId,
      modelData,
      artifactAvailability: uploadableArtifactStatus.hasUploadableArtifact
        ? "uploadable"
        : artifactSummary.sizeBytes > 0
          ? "not-uploadable"
          : "empty-or-missing",
      artifactSizeBytes: artifactSummary.sizeBytes,
      artifactDurationMs: artifactSummary.durationMs,
      artifactPartCount: artifactSummary.partCount,
      hasUploadableArtifact: uploadableArtifactStatus.hasUploadableArtifact,
      extraInfo: {
        trashContext: context,
        associatedNoteId,
        isActiveRecording,
        pipelineStage: pipelineSnapshot?.stage ?? null,
        pipelineStageStatus: pipelineSnapshot?.stageStatus ?? null,
        pipelineTerminalOutcome: pipelineSnapshot?.terminalOutcome ?? null,
        pipelineDiagnosticKind: pipelineSnapshot?.diagnostic?.kind ?? null,
      },
    });
  }

  /**
   * Explicit source-upload trigger used by debug/admin flows and upload retry
   * affordances. This does not own orchestration; it only runs the upload
   * worker for the captured source artifact.
   */
  async startSourceUpload({
    audioRecordingId,
    forceRestart,
  }: {
    audioRecordingId: Uuid;
    forceRestart?: boolean;
  }): Promise<void> {
    await this.uploading.enqueue({ audioRecordingId, forceRestart });
  }

  /**
   * Canonical manual entrypoint for post-recording processing. Hands the
   * recording back to the pipeline, which decides whether it can use the local
   * transcript or must upload + retranscribe first.
   */
  async processRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.pipeline.startProcessing({ audioRecordingId });
  }

  private async deleteSourceForRecording({
    audioRecordingId,
    noteId,
  }: {
    audioRecordingId: Uuid;
    noteId: Uuid | null;
  }): Promise<void> {
    if (!noteId) return;

    try {
      const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
        associatedAudioRecordingId: audioRecordingId,
      });
      const sessionId = uuidModule.resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId,
      });
      const sessionArtifactId = uuidModule.resolveSessionArtifactIdForCapturedAudio({
        sessionId,
        capturedAudioId,
      });
      const sourceId = uuidModule.resolveSourceIdForSessionArtifact({
        sessionId,
        sessionArtifactId,
      });

      await deleteNoteSource({ store: this.store, noteId, sourceId });
      await new DeleteSourceOperation({
        store: this.store,
        payload: { source_id: sourceId },
      }).execute();
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] Failed to delete audio source",
        info: {
          audioRecordingId,
          noteId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  getUploadStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingUploadingState {
    return this.uploading.getProgress({ audioRecordingId });
  }

  getTransformStateForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingTransformingState {
    return this.transforming.getTransformState({ audioRecordingId });
  }

  private async withRecordingTabLock({
    audioRecordingId,
    action,
    toastMessage,
  }: RecordingLockAction): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
      await action();
    } catch (unknownErr) {
      await this.clearActiveAudioRecording({
        audioRecordingId,
      });
      await this.releaseAudioRecordingLock({ audioRecordingId });

      if (unknownErr instanceof AudioRecorderLockingError) {
        toastModule.triggerToast({
          content: toastMessage,
          toastId: audioRecordingId,
        });

        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] recording action failed",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  private async setActiveAudioRecording({
    audioRecordingId,
    associatedNoteId,
  }: {
    audioRecordingId: Uuid;
    associatedNoteId?: Uuid | null;
  }): Promise<void> {
    await this.store.globalCache.set(CacheKey.ActiveAudioRecording, { audioRecordingId });
    runInAction(() => {
      this.activeRecordingIdValue = audioRecordingId;
      if (associatedNoteId !== undefined) {
        this.activeRecordingNoteIdValue = associatedNoteId;
      }
    });
  }

  private async clearActiveAudioRecording({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] clearActiveAudioRecording: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    const ownsLock = this.hasLockInCurrentTab;

    if (ownsLock) {
      await this.store.globalCache.del(CacheKey.ActiveAudioRecording);
    }
    runInAction(() => {
      this.activeRecordingIdValue = null;
      this.activeRecordingNoteIdValue = null;
    });
  }

  private async obtainAudioRecordingLock(): Promise<void> {
    if (this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] obtainAudioRecordingLock: already locked",
      });

      return;
    }

    const lockHandle = await audioRecordingModule.takeLock();
    runInAction(() => {
      this.activeRecordingLockHandle = lockHandle;
    });
  }

  private async maybeObtainAudioRecordingLock(): Promise<void> {
    try {
      await this.obtainAudioRecordingLock();
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message: "[AppStoreAudioStore] maybeObtainAudioRecordingLock: failed to obtain lock",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      throw unknownErr;
    }
  }

  private async releaseAudioRecordingLock({
    audioRecordingId,
  }: {
    audioRecordingId?: Uuid | null;
  }): Promise<void> {
    /**
     * If a value is provided...
     * Then we perform a check to ensure that the value is the same as the active recording id.
     */
    if (audioRecordingId && this.activeRecordingIdValue) {
      if (audioRecordingId !== this.activeRecordingIdValue) {
        logger.info({
          message: "[AppStoreAudioStore] releaseAudioRecordingLock: active recording id mismatch",
          info: {
            audioRecordingId,
            activeRecordingId: this.activeRecordingIdValue,
          },
        });

        return;
      }
    }

    if (!this.activeRecordingLockHandle) {
      logger.info({
        message: "[AppStoreAudioStore] releaseAudioRecordingLock: no lock to release",
      });

      return;
    }

    await audioRecordingModule.releaseLock(this.activeRecordingLockHandle);
    runInAction(() => {
      this.activeRecordingLockHandle = null;
    });
  }

  async initialize(): Promise<void> {
    await this.rehydrateAndObtainAudioRecordingLockIfNecessary();
    const activeRecordingId = this.activeRecordingId;

    try {
      await this.recording.rehydrateActiveDesktopRecordingState({
        audioRecordingId: activeRecordingId,
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] active desktop recording recovery failed during initialize",
        info: {
          audioRecordingId: activeRecordingId,
          hadActiveRecordingLock: this.hasLockInCurrentTab,
          error: objectModule.safeErrorAsJson(error as Error),
        },
      });

      if (activeRecordingId) {
        await this.cleanupActiveDesktopRecordingAfterRecoveryFailure({
          audioRecordingId: activeRecordingId,
        });
      }
    }

    await this.reconcilePersistedAudioState();
  }

  private async cleanupActiveDesktopRecordingAfterRecoveryFailure({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<void> {
    try {
      await electronModule.recall.stopRecordingSession({ audioRecordingId });
    } catch (stopError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to stop active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(stopError as Error),
        },
      });
    }

    try {
      await this.trashRecording({
        audioRecordingId,
        context: { reason: TrashRecordingReason.ActiveRecordingRecoveryFailed },
      });
      return;
    } catch (trashError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to trash active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(trashError as Error),
        },
      });
    }

    try {
      await this.clearActiveAudioRecording({ audioRecordingId });
    } catch (clearError) {
      logger.warn({
        message: "[AppStoreAudioStore] failed to clear active recording after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(clearError as Error),
        },
      });
    }

    try {
      await this.releaseAudioRecordingLock({ audioRecordingId });
    } catch (releaseError) {
      logger.warn({
        message:
          "[AppStoreAudioStore] failed to release active recording lock after recovery failure",
        info: {
          audioRecordingId,
          error: objectModule.safeErrorAsJson(releaseError as Error),
        },
      });
    }
  }

  async rehydrateAndObtainAudioRecordingLockIfNecessary(): Promise<void> {
    let cachedData = await this.store.globalCache.get(CacheKey.ActiveAudioRecording);

    if (!cachedData) {
      // Try legacy cache for migration
      const legacyData = await LEGACY_GLOBAL_CACHE_INSTANCE.get(CacheKey.ActiveAudioRecording);
      if (legacyData) {
        await this.store.globalCache.set(CacheKey.ActiveAudioRecording, legacyData);

        // Clear legacy cache after migration
        await LEGACY_GLOBAL_CACHE_INSTANCE.del(CacheKey.ActiveAudioRecording);

        cachedData = legacyData;
      }
    }

    const cachedAudioRecordingId = cachedData?.audioRecordingId;
    if (!cachedAudioRecordingId) return;

    /**
     * We attempt to obtain the audio recording lock on-boot.
     *
     * The way it works is:
     * - Only one tab is allowed to access the "active recording" at a time.
     * - An active recording is defined as an audio-recording that is either recording or paused.
     * - The id is stored in the cache (dexie) and observed for changes.
     * - On boot, we start observing it.
     * - If it is in the active state (recording or paused) we obtain the lock.
     * - First tab to do this becomes the "primary" tab.
     */
    const isActivelyRecording =
      await this.store.dexie.repositories.audioRecordings.isInRecordingState({
        id: cachedAudioRecordingId,
      });

    if (!isActivelyRecording) {
      return;
    }

    try {
      await this.obtainAudioRecordingLock();
      await this.setActiveAudioRecording({ audioRecordingId: cachedAudioRecordingId });
    } catch (unknownErr) {
      if (unknownErr instanceof AudioRecorderLockingError) {
        return;
      }

      logger.warn({
        message:
          "[AppStoreAudioStore] rehydrateAndObtainAudioRecordingLockIfNecessary: failed to obtain lock",
        info: {
          error: objectModule.safeErrorAsJson(unknownErr as Error),
        },
      });

      throw unknownErr;
    }
  }

  private async reconcilePersistedAudioState(): Promise<void> {
    try {
      const { clientGroupId } = await clientEnvModule.clientGroupId();
      const localTable = this.store.audioRecordings.localTable;
      const records: IndexedAudioRecordingSyncUpdateValue[] = await localTable
        .where("associated_client_id")
        .equals(clientGroupId)
        .filter(row => row.trashed_at === null)
        .toArray();

      for (const record of records) {
        await this.reconcileRecordingState(record);
      }
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] reconcilePersistedAudioState: failed",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  private async reconcileRecordingState(
    record: IndexedAudioRecordingSyncUpdateValue
  ): Promise<void> {
    const audioRecordingId = record.model_id as Uuid;
    const modelData = record.model_data;

    if (modelData.trashed_at) {
      return;
    }

    if (modelData.recording_status !== AudioRecordingRecordingStatus.COMPLETED) {
      return;
    }

    if (!modelData.associated_note_id) {
      return;
    }

    if (this.hasTerminalProcessingStatus({ modelData })) {
      if (!this.hasTerminalUploadStatus({ modelData })) {
        void this.uploading.enqueue({ audioRecordingId });
      }

      return;
    }

    if (this.hasUploadStatusThatBlocksProcessing({ modelData })) {
      return;
    }

    try {
      this.pipeline.startProcessing({ audioRecordingId });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioStore] reconcileRecordingState: failed",
        info: {
          audioRecordingId,
          uploadStatus: modelData.upload_status,
          processingStatus: modelData.processing_status,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  private hasTerminalProcessingStatus({ modelData }: { modelData: AudioRecordingModelData }) {
    return (
      modelData.processing_status === AudioRecordingProcessingStatus.ERROR ||
      modelData.processing_status === AudioRecordingProcessingStatus.COMPLETED
    );
  }

  private hasTerminalUploadStatus({ modelData }: { modelData: AudioRecordingModelData }) {
    return (
      modelData.upload_status === AudioRecordingUploadStatus.COMPLETED ||
      modelData.upload_status === AudioRecordingUploadStatus.ERROR ||
      modelData.upload_status === AudioRecordingUploadStatus.SKIPPED
    );
  }

  private hasUploadStatusThatBlocksProcessing({
    modelData,
  }: {
    modelData: AudioRecordingModelData;
  }) {
    return (
      modelData.upload_status === AudioRecordingUploadStatus.ERROR ||
      modelData.upload_status === AudioRecordingUploadStatus.SKIPPED
    );
  }

  async dispose(): Promise<void> {
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async resetState(): Promise<void> {
    await this.maybeObtainAudioRecordingLock();

    this.ui.resetState();
    this.recording.resetState();
    this.uploading.resetState();
    this.transforming.resetState();
    this.pipeline.resetState();

    await this.clearActiveAudioRecording({ audioRecordingId: null });
    await this.releaseAudioRecordingLock({ audioRecordingId: null });
  }

  async ensureActiveRecordingStateIsValid(): Promise<void> {
    if (!this.activeRecordingId) return;

    /**
     * If we have an activeRecordingId, but no active recording on the device, we'll just
     * try resetting the state.
     *
     * The active recording should always be "in progress".
     * If it hasn't been started, or it has been completed, we'll clear it out.
     *
     * Note that - if a note doesn't have an `associatedNoteId`, this will also clear it out.
     * (This may become a valid state in the future, but for now it is always expected to be associated
     * with a note.)
     *
     * The recording should also always be from the current client device.
     */
    if (
      !this.activeRecording ||
      this.activeRecording.isTrashed ||
      !this.activeRecording.hasStartedRecording ||
      this.activeRecording.hasBeenCompleted ||
      !this.activeRecording.associatedNoteId ||
      !(await this.activeRecording.isFromCurrentClientDevice())
    ) {
      await this.resetState();
      return;
    }
  }

  async _activateRecording({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.setActiveAudioRecording({ audioRecordingId });
    await this.obtainAudioRecordingLock();
  }

  async _deactivateRecording(): Promise<void> {
    const activeRecordingId = this.activeRecordingIdValue;
    await this.clearActiveAudioRecording({ audioRecordingId: activeRecordingId });
    await this.releaseAudioRecordingLock({ audioRecordingId: activeRecordingId });
  }

  private get hasLockInCurrentTab(): boolean {
    return this.activeRecordingLockHandle !== null;
  }
}
